import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { GET_WORK_ORDER_OPERATIONS } from 'lib/api/queries';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getMachineScheduleWooRefs } from 'lib/selectors/getMachineSchedule';
import { getMachine } from 'lib/selectors/getMachine';
import { getSelectWorkOrderOpSearchMachineRefs } from 'lib/selectors/getSelectWorkOrderOpSearchMachineRefs';
import {
  parseSearchFields,
  isBarcodeFormattedSearch,
} from 'lib/utils/workOrderOperationSearch';
import {
  provideFallbackForCycleTimeMs,
  trim,
  buildGraphQLWhereClause,
} from '../helpers';

const useRequestUnscheduledWorkOrderOps = ({
  onlyAssignedToThisMachine = false,
  onlyNotAssignedToThisMachine = false,
  skip = false,
} = {}) => {
  const machine = useSelector(getMachine);
  const currentOrderOperation = useSelector(getCurrentWorkOrderOp);
  const machineScheduleWooRefs = useSelector(getMachineScheduleWooRefs);
  const { search } = useSelector(getSelectWorkOrderOpOptions);
  const selectWorkOrderOpSearchMachineRefs = useSelector(
    getSelectWorkOrderOpSearchMachineRefs
  );

  const currentOrderOperationRef = currentOrderOperation?.workOrderOperationRef;
  const scheduleWorkOrderOperationRefs = useMemo(() => {
    return [...machineScheduleWooRefs, currentOrderOperationRef].filter(
      Boolean
    );
  }, [machineScheduleWooRefs, currentOrderOperationRef]);

  const [operations, setOperations] = useState(null);

  const getSearchWhereClause = useCallback(
    (value) => {
      if (!value) {
        return {};
      }

      // Check if search has barcode field prefixes (e.g., "workOrderId: 123")
      const isBarcodeFormat = isBarcodeFormattedSearch(value);

      if (isBarcodeFormat) {
        // Parse and use AND-based filtering for barcode searches
        const parsedFields = parseSearchFields(value);
        const whereClause = buildGraphQLWhereClause(parsedFields, trim);

        // If we have field-specific filters, return them
        if (Object.keys(whereClause).length > 0) {
          return whereClause;
        }
      }

      // Use original OR-based search for plain text
      return {
        _or: [
          { workOrderId: { _ilike: `%${trim(value)}%` } },
          { workOrder: { partNumber: { _ilike: `%${trim(value)}%` } } },
          { resource: { resourceId: { _ilike: `%${trim(value)}%` } } },
          {
            resource: {
              machineRef: { _in: selectWorkOrderOpSearchMachineRefs },
            },
          },
        ].filter(Boolean),
      };
    },
    [selectWorkOrderOpSearchMachineRefs]
  );

  const [
    requestMachineOrderOperations,
    { variables: { limit = 50 } = {}, error, loading },
  ] = useLazyQuery(GET_WORK_ORDER_OPERATIONS, {
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      if (res.erpWorkOrderOperations) {
        const withCycleTimeMsFallback = provideFallbackForCycleTimeMs(
          res.erpWorkOrderOperations
        );

        setOperations(withCycleTimeMsFallback);
      }
    },
  });

  // Wrapper to add machine schedule wo refs to where clause
  // This is to prevent showing operations that are already scheduled for this machine
  const requestUnscheduledWorkOrderOpsWrapper = useCallback(
    ({ variables }) => {
      const { erpResources } = machine || {};

      const where = {
        _and: [
          variables.where,
          scheduleWorkOrderOperationRefs?.length && {
            workOrderOperationRef: {
              _nin: scheduleWorkOrderOperationRefs,
            },
          },
          onlyAssignedToThisMachine && {
            resource: {
              resourceId: {
                _in: erpResources,
              },
            },
          },
          onlyNotAssignedToThisMachine && {
            resource: {
              resourceId: {
                _nin: erpResources,
              },
            },
          },
        ].filter(Boolean),
      };
      return requestMachineOrderOperations({
        variables: {
          ...variables,
          where,
        },
      });
    },
    [
      requestMachineOrderOperations,
      scheduleWorkOrderOperationRefs,
      onlyAssignedToThisMachine,
      onlyNotAssignedToThisMachine,
      machine,
    ]
  );

  const queryAll = useCallback(
    ({ queryLimit = 50, searchClause } = {}) => {
      return requestUnscheduledWorkOrderOpsWrapper({
        variables: {
          where: {
            ...searchClause,
            closedDate: { _is_null: true },
            status: { _nin: ['C', 'X', 'COMPLETED'] },
          },
          limit: queryLimit,
        },
      });
    },
    [requestUnscheduledWorkOrderOpsWrapper]
  );

  const requestUnscheduledWorkOrderOps = useCallback(
    ({ queryLimit = 50 } = {}) => {
      const newSearch = getSearchWhereClause(search);

      return queryAll({
        searchClause: newSearch,
        queryLimit,
      });
    },
    [search, queryAll, getSearchWhereClause]
  );

  useEffect(() => {
    if (!skip) {
      requestUnscheduledWorkOrderOps();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [skip]);

  return [
    requestUnscheduledWorkOrderOps,
    {
      operations: operations?.length && !skip ? operations : [],
      loading,
      error,
      limit,
    },
  ];
};

export default useRequestUnscheduledWorkOrderOps;
